import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CreateReportV2Component } from '../../components/chart-data-v2/chart-data';
import { ReportV2Service } from '../../services/report-v2.service';
import { ReportStatus } from '../../models/report';
import { Router } from '@angular/router';
import { Loader } from '../../loader/loader';
import { SnackbarService } from '../../services/snackbar.service';
import { DialogueBox } from '../../dialogue-box/dialogue-box';
import { AuthService } from '../../auth/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  CellClickedEvent,
  GridOptions,
  BodyScrollEndEvent,
  GetRowIdParams,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-report-list',
  standalone: true,
  imports: [
    CommonModule,
    CreateReportV2Component,
    Loader,
    DialogueBox,
    FormsModule,
    ReactiveFormsModule,
    AgGridAngular,
  ],
  templateUrl: './report-list.html',
  styleUrl: './report-list.scss',
})
export class ReportList {
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private reportV2Service = inject(ReportV2Service);
  private router = inject(Router);
  private snackbar = inject(SnackbarService);
  isLoading = false;
  reports = this.reportV2Service.getReports();
  currentUserName!: string | null;
  selectedReportId: number | 0 = 0;
  isClosing = false;
  projectId: number | null = null;
  reportTypeId: number | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  isDialogOpen = false;
  isFilterEnabled = false;
  name: string = '';
  dialogTexts = DialogueTexts;
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;

  private currentPage = 1;
  private readonly pageSize = 20;
  private isLoadingMore = false;
  private allLoaded = false;

  ngOnInit(): void {
    this.reportV2Service.reportsLoadCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ append }) => {
        if (!append) {
          this.isLoading = false;
        }
      });

    this.authService
      .getCurrentAccount()
      .then((currentAccount) => {
        if (currentAccount) {
          this.name = currentAccount.name || '';
          const parts = this.name.split(/\s+/).filter(Boolean).slice(0, 2);
          this.name = parts.reverse().join(' ').replace(/,$/, '').trim();
        }
      })
      .catch((error) => {
        // Prevent uncaught promise rejections from bubbling into the console.
        console.warn('[ReportList] Failed to resolve current account', error);
        this.name = '';
      });
    this.reportV2Service.loadReports(1, this.pageSize);
  }

  columnDefs: ColDef[] = [
    {
      headerName: 'Report ID',
      field: 'reportId',
      width: 130,
      minWidth: 130,
      maxWidth: 130,
    },
    {
      headerName: 'Project ID',
      field: 'projectId',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
    },
    {
      headerName: 'Date Created',
      field: 'dateCreated',
      width: 160,
      minWidth: 160,
      maxWidth: 160,
      valueFormatter: (p: ValueFormatterParams) =>
        p.value ? new Date(p.value as string).toLocaleDateString('en-US') : '',
    },
    { headerName: 'Report Name', field: 'reportName' },

    { headerName: 'Client Name', field: 'clientName' },
    {
      headerName: 'Owner',
      field: 'owner',
      valueFormatter: (p: ValueFormatterParams) => p.value?.displayName ?? '',
    },
    {
      headerName: 'Status',
      cellClass: 'status-cell',
      valueGetter: (params) => params.context.componentParent.getDisplayStatus(params.data),
      cellRenderer: (params: ICellRendererParams) => {
        const statusValue = params.data.status;
        const displayStatus = params.value ?? '';

        const icons = params.context.componentParent
          .getStatusDotsIcons(statusValue)
          .map((icon: string) => `<img src="/assets/icons/${icon}" width="3" height="3" />`)
          .join('');

        const statusClass = statusValue === 6 ? 'status-red' : '';
        return `
          <div class="status">
            <span class="status-text ${statusClass}">${displayStatus}</span>
            <div class="dots">${icons}</div>
          </div>
        `;
      },
    },
    {
      headerName: 'Actions',
      cellClass: 'actions-cell',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      cellRenderer: (params: ICellRendererParams) => {
        const statusValue = params.data.status;
        const statusLabel = params.data.statusLabel || '';
        const isPublished =
          statusValue === ReportStatus.DocsGenerated || statusLabel === 'Published';
        const isInitiated = statusValue === ReportStatus.Initiated;
        const isFailed = statusValue === ReportStatus.Failed;
        const isEditDisabled = isPublished || isInitiated;
        const primaryAction = isFailed ? 'retry' : 'edit';
        const primaryActionTitle = isFailed ? 'Retry' : 'Edit';
        const primaryActionIcon = isFailed ? 'retry_32.svg' : 'edit_32.svg';
        const pdf = params.data.docFileUrl;

        return `
          <div class="actions">
    
            <!-- Edit Button -->
            <button class="edit"
              data-action="${primaryAction}"
              title="${primaryActionTitle}"
              ${isEditDisabled ? 'disabled' : ''}>
              <img src="/assets/icons/${primaryActionIcon}" alt="${primaryActionTitle}" />
            </button>
    
            <!-- Delete Button -->
            ${
              statusValue !== ReportStatus.Approved && !isPublished
                ? `<button data-action="delete">
                  <img src="/assets/icons/delete_32.svg" />
               </button>`
                : ''
            }
    
            <!-- DOC + PDF Buttons (Published only) -->
            ${
              isPublished
                ? `
                  <button data-action="doc"
                    ${!pdf ? 'disabled title="Document Generation In Progress"' : ''}>
                    <img src="/assets/icons/download.svg" />
                  </button>
                `
                : ''
            }
    
          </div>
        `;
      },

      filter: false,

      onCellClicked: (params: CellClickedEvent) => {
        const event = params.event as Event | null;
        if (!event) return;

        const target = event.target as HTMLElement | null;
        if (!target) return;

        const btn = target.closest('button');
        if (!btn) return;

        const action = btn.getAttribute('data-action');
        if (!action) return;

        switch (action) {
          case 'edit':
            params.context.componentParent.editReport(params.data.reportId, params.data.status);
            break;
          case 'retry':
            params.context.componentParent.retryReportGeneration(params.data.reportId);
            break;

          case 'delete':
            params.context.componentParent.openDialog(params.data.reportId);
            break;
          case 'doc':
            params.context.componentParent.downloadFile(params.data.docFileUrl);
            break;
        }
      },
    },
  ];

  gridOptions: GridOptions = {
    rowHeight: 55,
    rowStyle: {
      'border-bottom': '10px solid #ffffff',
    },
    domLayout: 'normal',
    // Keep viewport position when additional rows are appended.
    suppressScrollOnNewData: true,
    // Stable row identity prevents full grid resets on signal updates.
    getRowId: (params: GetRowIdParams) => {
      const reportId = params.data?.reportId;
      if (reportId == null) {
        throw new Error('Missing reportId in report-list row data');
      }
      return String(reportId);
    },
  };

  getStatusDotsIcons(status: number): string[] {
    if (status === ReportStatus.DocsGenerated) {
      return Array(4).fill('statusicon_emerald_green.svg');
    }
    const dots: string[] = [];

    for (let i = 1; i <= 4; i++) {
      if (status === 10) {
        dots.push('statusicon_green.svg');
      } else if (status === 3) {
        dots.push(i <= 3 ? 'statusicon_yellow.svg' : 'statusicon_white.svg');
      } else if (status === 2) {
        dots.push(i <= 2 ? 'statusicon_orange.svg' : 'statusicon_white.svg');
      } else if (status === 1) {
        dots.push(i <= 1 ? 'statusicon_black.svg' : 'statusicon_white.svg');
      } else {
        dots.push('statusicon_white.svg');
      }
    }

    return dots;
  }

  downloadFile(filePath: string) {
    const link = document.createElement('a');
    link.href = filePath;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    // Do not use fetch/blob here; direct navigation avoids CORS restrictions.
    link.click();
  }

  loadReports() {
    this.currentPage = 1;
    this.allLoaded = false;
    this.isLoading = true;
    this.reportV2Service.loadReports(1, this.pageSize);
  }

  onGridBodyScrollEnd(event: BodyScrollEndEvent) {
    if (this.isLoadingMore || this.allLoaded) return;
    if (event.direction !== 'vertical') return;

    const api = event.api;
    const lastRow = api.getLastDisplayedRowIndex();
    const totalRows = api.getDisplayedRowCount();

    if (totalRows === 0) return;

    const scrollPercent = (lastRow + 1) / totalRows;
    if (scrollPercent >= 0.8) {
      this.isLoadingMore = true;
      this.currentPage++;
      this.reportV2Service.loadReports(this.currentPage, this.pageSize, true);
      setTimeout(() => {
        this.isLoadingMore = false;
      }, 1000);
    }
  }

  formatDate(date: Date | null): string | null {
    if (!date) return null;
    return date.toISOString();
  }

  enableGridFilters() {
    this.isFilterEnabled = !this.isFilterEnabled;
  }

  openDialog(reportId: number) {
    this.isDialogOpen = true;
    this.selectedReportId = reportId;
  }

  closeDialog() {
    this.isDialogOpen = false;
  }

  getStatusDisplay(status: ReportStatus): string {
    return ReportStatus[status] ?? '';
  }

  getDisplayStatus(report: {
    status?: ReportStatus;
    statusLabel?: string;
    clientName?: string;
  }): string {
    const status = report.statusLabel || this.getStatusDisplay(report.status as ReportStatus);
    const clientName = (report.clientName || '').toString().trim().toLowerCase();
    return clientName === 'chevron' && status === 'Generated' ? 'Analyzed' : status;
  }

  private editReport(reportId: number, status: ReportStatus) {
    // Edit opens screen 03 (review & refine) of the update-from-data flow.
    if (status === ReportStatus.Generated || status === ReportStatus.Review) {
      this.router.navigate(['/report-review'], {
        queryParams: { reportId: reportId },
      });
    }
    if (status === ReportStatus.Download) {
      this.router.navigate(['/report-compile'], { queryParams: { reportId: reportId } });
    }
  }

  private retryReportGeneration(reportId: number) {
    this.isLoading = true;
    this.reportV2Service.retryReportGeneration(reportId).subscribe({
      next: (response) => {
        if (response.success) {
          this.updateReportStatus(reportId);
          this.snackbar.showCustom({
            type: 'success',
            title: ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
            message: response.message ||ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
          });
          this.isLoading = false;
          return;
        }

        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: response.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  private updateReportStatus(reportId: number) {
    this.reportV2Service
      .updateReportStatus(reportId, { status: ReportStatus.Initiated })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.reportV2Service.patchReportInList(reportId, {
              status: ReportStatus.Initiated,
              statusLabel: this.getStatusDisplay(ReportStatus.Initiated),
            });
            return;
          }

          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: response.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        },
        error: (err) => {
          const errorMessage =
            err?.error?.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG;
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: errorMessage,
          });
        },
      });
  }

  deleteReport(reportId: number) {
    this.isDialogOpen = false;
    this.isLoading = true;
    this.reportV2Service.deleteReport(reportId).subscribe({
      next: (response) => {
        this.loadReports();
        this.snackbar.showCustom({
          type: 'success',
          title: this.validationMessages.TOASTER.SUCCESS.TITLE,
          message: response.message,
        });
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  previewDoc(documentUrl: string, reportname: string, wordDocumentPath?: string) {
    this.router.navigate(['/preview-report'], {
      queryParams: {
        documentUrl: encodeURIComponent(documentUrl),
        reportname: reportname,
        downloadOverrideUrl: wordDocumentPath ? encodeURIComponent(wordDocumentPath) : undefined,
      },
      state: { fromPdfViewer: true, returnUrl: this.router.url },
    });
  }
}
